import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, MapPin, CreditCard, Building, ArrowRight } from 'lucide-react';
const reasons = [{
  icon: ShieldCheck,
  title: 'Trusted Developer',
  short: '10+ years of excellence',
  desc: 'Over a decade of excellence in delivering premium real estate solutions with complete transparency and unwavering commitment to quality.',
  stat: '10+',
  statLabel: 'Years'
}, {
  icon: Building,
  title: 'Verified Properties',
  short: 'Legal & structural checks',
  desc: 'Every property in our portfolio undergoes rigorous legal verification, structural assessments, and quality control before listing.',
  stat: '100%',
  statLabel: 'Verified'
}, {
  icon: CreditCard,
  title: 'Flexible Payment',
  short: 'Tailored to your needs',
  desc: 'We offer customized payment structures, mortgage partnerships, and installment plans to make property acquisition seamless.',
  stat: '6-24',
  statLabel: 'Months'
}, {
  icon: MapPin,
  title: 'Premium Locations',
  short: 'Prime real estate spots',
  desc: 'Strategically situated estates in Lagos, Ibeju-Lekki, Ikoyi and more — locations that guarantee high ROI and exceptional living.',
  stat: '15+',
  statLabel: 'Cities'
}];
export function WhyChoose() {
  const [activeIndex, setActiveIndex] = useState(0);
  return <section className="py-24 px-6 md:px-12 max-w-[1280px] mx-auto bg-bone" data-id="element-718">
      <div className="mb-16 max-w-2xl" data-id="element-719">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-xs font-semibold tracking-[0.2em] uppercase text-gold mb-4" data-id="element-720">
          Why Zylus
        </motion.div>
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-4xl md:text-5xl font-serif text-ink tracking-tight mb-4" data-id="element-721">
          Built on trust, designed for you.
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-ink/60" data-id="element-722">
          Hover or tap a pillar to explore what makes Zylus the most trusted
          name in premium real estate.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-723">
        {/* Tabs Column */}
        <div className="lg:col-span-5 flex flex-col gap-3" data-id="element-724">
          {reasons.map((reason, i) => {
          const isActive = activeIndex === i;
          const Icon = reason.icon;
          return <motion.button key={i} onMouseEnter={() => setActiveIndex(i)} onClick={() => setActiveIndex(i)} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className={`relative text-left rounded-2xl p-6 border transition-all duration-500 overflow-hidden ${isActive ? 'bg-ink border-ink text-white shadow-lg' : 'bg-white border-ink/5 text-ink hover:border-ink/20'}`} data-id="element-725">
                <div className="flex items-center gap-4" data-id="element-726">
                  <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${isActive ? 'bg-gold' : 'bg-bone'}`} data-id="element-727">
                    <Icon className={`w-6 h-6 transition-colors ${isActive ? 'text-white' : 'text-gold'}`} strokeWidth={1.5} data-id="element-728" />
                  </div>
                  <div className="flex-1" data-id="element-729">
                    <div className="flex items-center justify-between" data-id="element-730">
                      <h3 className="text-lg font-serif" data-id="element-731">{reason.title}</h3>
                      <motion.div animate={{
                    rotate: isActive ? 0 : -45,
                    opacity: isActive ? 1 : 0.4
                  }} className="shrink-0" data-id="element-732">
                        <ArrowRight className={`w-4 h-4 ${isActive ? 'text-gold' : 'text-ink/40'}`} data-id="element-733" />
                      </motion.div>
                    </div>
                    <p className={`text-sm mt-1 ${isActive ? 'text-white/60' : 'text-ink/50'}`} data-id="element-734">
                      {reason.short}
                    </p>
                  </div>
                </div>

                {/* Active progress bar */}
                {isActive && <motion.div layoutId="active-bar" className="absolute bottom-0 left-0 h-[2px] bg-gold" initial={{
              width: '0%'
            }} animate={{
              width: '100%'
            }} transition={{
              duration: 0.6
            }} data-id="element-735" />}
              </motion.button>;
        })}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-7 relative rounded-2xl overflow-hidden bg-white border border-ink/5 min-h-[420px]" data-id="element-736">
          <AnimatePresence mode="wait" data-id="element-737">
            <motion.div key={activeIndex} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute inset-0 p-10 flex flex-col justify-between" data-id="element-738">
              <div data-id="element-739">
                <div className="flex items-baseline gap-3 mb-8" data-id="element-740">
                  <span className="text-7xl md:text-8xl font-serif text-gold leading-none" data-id="element-741">
                    {reasons[activeIndex].stat}
                  </span>
                  <span className="text-sm font-semibold tracking-widest uppercase text-ink/50" data-id="element-742">
                    {reasons[activeIndex].statLabel}
                  </span>
                </div>

                <h3 className="text-3xl md:text-4xl font-serif text-ink mb-4" data-id="element-743">
                  {reasons[activeIndex].title}
                </h3>
                <p className="text-ink/70 leading-relaxed text-lg max-w-lg" data-id="element-744">
                  {reasons[activeIndex].desc}
                </p>
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-ink/5" data-id="element-745">
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-ink/40" data-id="element-746">
                  0{activeIndex + 1} / 0{reasons.length}
                </div>
                <a href="#book" className="text-sm font-medium text-ink hover:text-gold transition-colors flex items-center gap-2 group" data-id="element-747">
                  Learn more
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-748" />
                </a>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Decorative corner */}
          <div className="absolute top-6 right-6 w-20 h-20 rounded-full border border-gold/20 pointer-events-none" data-id="element-749" />
          <div className="absolute top-10 right-10 w-12 h-12 rounded-full bg-gold/5 pointer-events-none" data-id="element-750" />
        </div>
      </div>
    </section>;
}